import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import SignedInSettingsShell from "@/components/shared/SignedInSettingsShell";
import FreeExtrasSection from "@/components/account/FreeExtrasSection";
import DeveloperAccessSection from "@/components/account/DeveloperAccessSection";
import McpConnectionsSection from "@/components/account/McpConnectionsSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getBillingSummary,
  getCurrentAccountId,
  getPreferencesBootstrap,
  listMyAccounts,
  readCachedBillingSummary,
  readCachedPreferencesBootstrap,
  saveAccountLanguages,
  setCurrentAccountId,
  setMyPrimaryAccount,
} from "@/lib/api";
import { buildSecondaryLanguageOptions } from "@/lib/account-linking";
import { useI18n } from "@/lib/i18n";
import {
  buildLanguageSelectOptions,
  groupLanguagePreferences,
  normalizeLanguageCode,
} from "@/lib/languages";
import { resolveOwnedAccountId } from "@/lib/owned-account-selection";
import {
  redirectToAuthIfRequired,
  redirectToAuthIfSignedOut,
} from "@/lib/auth-required-redirect";
import { EXISTING_PREFERENCES_PATH } from "@/lib/preferences-route";
import { getPlanLabel } from "@/lib/plans";
import { formatSettingsPlanSummary } from "@/lib/settings-shell";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";
import { matchesBillingSummaryAccount } from "@/lib/billing-summary-match";
import { linkedAccountStatusLabel, visibleLinkedAccounts } from "@/lib/account-visibility";

const HOME_LANGUAGE_UNSET = "__unset__";

function readUserEmail() {
  const client = getSupabaseBrowserClient();
  if (!client) {
    return Promise.resolve("");
  }
  return client.auth.getSession()
    .then(({ data }) => {
      const email = String(data?.session?.user?.email || "").trim();
      if (email) {
        return email;
      }
      return client.auth.getUser()
        .then(({ data: userData }) => String(data?.session?.user?.email || userData?.user?.email || "").trim());
    })
    .catch(() => "");
}

function buildAccountLabel(account) {
  return account?.displayName || account?.phoneNumber || "Linked number";
}

export default function Account() {
  const { locale, tx } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryAccountId = searchParams.get("accountId") || "";
  const cachedAccountsPayload = null;
  const initialActiveAccountId = queryAccountId || getCurrentAccountId() || "";
  const initialBillingAccountId = initialActiveAccountId;
  const initialBillingSummary = null;
  const [accountsPayload, setAccountsPayload] = useState(cachedAccountsPayload);
  const [billingSummary, setBillingSummary] = useState(initialBillingSummary);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(!cachedAccountsPayload);
  const [billingSummaryLoading, setBillingSummaryLoading] = useState(
    Boolean(initialBillingAccountId) && !initialBillingSummary,
  );
  const [billingSummaryError, setBillingSummaryError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [primaryMutationAccountId, setPrimaryMutationAccountId] = useState("");
  const [activeAccountProfile, setActiveAccountProfile] = useState(null);
  const [activeAccountProfileLoading, setActiveAccountProfileLoading] = useState(false);
  const [languageDraft, setLanguageDraft] = useState({
    homeLanguageCode: "",
    understoodLanguageCodes: [],
  });
  const [languageSaving, setLanguageSaving] = useState(false);
  const [languageSaveMessage, setLanguageSaveMessage] = useState("");
  const relinked = searchParams.get("relinked") === "1";

  useEffect(() => {
    let cancelled = false;

    async function loadAccountSurface() {
      try {
        if (await redirectToAuthIfSignedOut({ navigate, location })) {
          return;
        }
        setLoading(!cachedAccountsPayload);
        setErrorMessage("");
        const [payload, authEmail] = await Promise.all([
          listMyAccounts({ forceFresh: true }),
          readUserEmail(),
        ]);
        if (!cancelled) {
          setAccountsPayload(payload);
          setEmail(authEmail);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Could not load your account.";
          if (/no linked account found/i.test(message)) {
            setAccountsPayload({
              accounts: [],
              primaryAccountId: null,
              billingAccountId: null,
              profile: null,
            });
            setEmail(await readUserEmail());
          } else {
            if (redirectToAuthIfRequired(error, { navigate, location })) {
              return;
            }
            setErrorMessage(message);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadAccountSurface();

    return () => {
      cancelled = true;
    };
  }, [cachedAccountsPayload, location, navigate]);

  const linkedNumbers = visibleLinkedAccounts(Array.isArray(accountsPayload?.accounts) ? accountsPayload.accounts : []);
  const accountSurfaceLoaded = Boolean(accountsPayload);
  const primaryAccountId = String(accountsPayload?.primaryAccountId || "").trim();
  const billingAccountId = String(accountsPayload?.billingAccountId || "").trim();
  const profile = accountsPayload?.profile || null;
  const activeAccountId = accountSurfaceLoaded ? resolveOwnedAccountId(linkedNumbers, { queryAccountId, storedAccountId: getCurrentAccountId(), primaryAccountId, fallbackAccountId: linkedNumbers[0]?.id }) : "";
  const activeLanguageProfile = activeAccountProfile || null;
  const billingRouteAccountId = billingAccountId || activeAccountId;
  const activeBillingSummary = matchesBillingSummaryAccount(billingSummary, billingRouteAccountId)
    ? billingSummary
    : null;
  const preferencesRoute = activeAccountId
    ? `${EXISTING_PREFERENCES_PATH}&accountId=${encodeURIComponent(activeAccountId)}`
    : EXISTING_PREFERENCES_PATH;
  const referralsRoute = activeAccountId
    ? `/referrals?accountId=${encodeURIComponent(activeAccountId)}`
    : "/referrals";
  const billingRoute = billingRouteAccountId
    ? `/billing?accountId=${encodeURIComponent(billingRouteAccountId)}`
    : "/billing";
  const fallbackPlanCode = linkedNumbers.find((account) => account.id === activeAccountId)?.plan || "free";
  const billingSummaryUnavailable = Boolean(billingSummaryError) && !activeBillingSummary;
  const planCode = activeBillingSummary?.currentPlan
    || (!billingRouteAccountId ? fallbackPlanCode : "");
  const planLabel = activeBillingSummary
    ? `${getPlanLabel(planCode)} plan`
    : billingSummaryUnavailable
      ? "Billing unavailable"
      : !billingRouteAccountId
        ? `${getPlanLabel(fallbackPlanCode)} plan`
        : "Loading…";
  const sortedNumbers = useMemo(
    () => [...linkedNumbers].sort((left, right) => {
      if (left.isPrimary && !right.isPrimary) return -1;
      if (!left.isPrimary && right.isPrimary) return 1;
      return buildAccountLabel(left).localeCompare(buildAccountLabel(right));
    }),
    [linkedNumbers],
  );
  const languageOptions = useMemo(
    () => buildLanguageSelectOptions({ preferredUiLanguageCode: locale, uiOnly: false }),
    [locale],
  );
  const normalizedHomeLanguageCode = normalizeLanguageCode(languageDraft.homeLanguageCode, "");
  const otherLanguageOptions = useMemo(
    () => buildSecondaryLanguageOptions(languageOptions, { homeLanguageCode: normalizedHomeLanguageCode }),
    [languageOptions, normalizedHomeLanguageCode],
  );

  useEffect(() => {
    if (accountSurfaceLoaded) setCurrentAccountId(activeAccountId);
  }, [accountSurfaceLoaded, activeAccountId]);

  useEffect(() => {
    if (!activeAccountId) {
      setActiveAccountProfile(null);
      setActiveAccountProfileLoading(false);
      return;
    }
    const cachedBootstrap = readCachedPreferencesBootstrap(activeAccountId);
    setActiveAccountProfile(cachedBootstrap?.profile || null);
    setActiveAccountProfileLoading(!cachedBootstrap?.profile);
    let cancelled = false;
    void getPreferencesBootstrap(activeAccountId)
      .then((payload) => {
        if (!cancelled) {
          setActiveAccountProfile(payload?.profile || null);
          setActiveAccountProfileLoading(false);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          if (redirectToAuthIfRequired(error, { navigate, location })) {
            return;
          }
          setActiveAccountProfile(null);
          setActiveAccountProfileLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeAccountId, location, navigate]);

  useEffect(() => {
    if (activeAccountProfileLoading && !activeLanguageProfile) {
      return;
    }
    setLanguageDraft(groupLanguagePreferences(activeLanguageProfile || {}));
    setLanguageSaveMessage("");
  }, [activeAccountProfileLoading, activeLanguageProfile]);

  useEffect(() => {
    if (!billingRouteAccountId) {
      setBillingSummary(null);
      setBillingSummaryLoading(false);
      setBillingSummaryError("");
      return;
    }
    let cancelled = false;
    const cachedRouteSummary = readCachedBillingSummary(billingRouteAccountId);
    const matchingCachedRouteSummary = matchesBillingSummaryAccount(
      cachedRouteSummary,
      billingRouteAccountId,
    )
      ? cachedRouteSummary
      : null;
    setBillingSummaryError("");
    setBillingSummary((current) => {
      if (matchesBillingSummaryAccount(current, billingRouteAccountId)) {
        return current;
      }
      return matchingCachedRouteSummary;
    });
    setBillingSummaryLoading(!matchingCachedRouteSummary);
    getBillingSummary(billingRouteAccountId, {
      forceFresh: !matchingCachedRouteSummary,
    })
      .then((payload) => {
        if (!cancelled) {
          setBillingSummary(payload);
          setBillingSummaryError("");
        }
      })
      .catch((error) => {
        if (!cancelled && !matchingCachedRouteSummary) {
          if (redirectToAuthIfRequired(error, { navigate, location })) {
            return;
          }
          setBillingSummary(null);
          setBillingSummaryError("Could not load your shared billing details right now.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBillingSummaryLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [billingRouteAccountId, location, navigate]);

  async function handleMakePrimary(accountId) {
    try {
      setPrimaryMutationAccountId(accountId);
      await setMyPrimaryAccount(accountId);
      setAccountsPayload((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          primaryAccountId: accountId,
          accounts: (Array.isArray(current.accounts) ? current.accounts : []).map((account) => ({
            ...account,
            isPrimary: account.id === accountId,
          })),
        };
      });
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return;
      }
      setErrorMessage(error instanceof Error ? error.message : "Could not update your primary number.");
    } finally {
      setPrimaryMutationAccountId("");
    }
  }

  async function handleSaveLanguages() {
    if (!activeAccountId || languageSaving) {
      return;
    }
    setLanguageSaving(true);
    setLanguageSaveMessage("");
    setErrorMessage("");
    try {
      const homeLanguageCode = normalizeLanguageCode(languageDraft.homeLanguageCode, "");
      const payload = await saveAccountLanguages(activeAccountId, {
        preferredUiLanguageCode: normalizeLanguageCode(locale),
        languagePreferences: {
          home: homeLanguageCode
            ? [
                {
                  languageCode: homeLanguageCode,
                  rank: 0,
                },
              ]
            : [],
          understands: languageDraft.understoodLanguageCodes.map((languageCode, index) => ({
            languageCode: normalizeLanguageCode(languageCode, ""),
            rank: index,
          })).filter((item) => item.languageCode),
        },
      });
      setAccountsPayload((current) => ({
        ...(current || {}),
        preferredUiLanguageCode: payload?.preferredUiLanguageCode || current?.preferredUiLanguageCode || locale,
        profile:
          activeAccountId && activeAccountId === String(current?.primaryAccountId || "").trim()
            ? {
                ...(current?.profile || {}),
                preferredUiLanguageCode: payload?.preferredUiLanguageCode || current?.profile?.preferredUiLanguageCode || locale,
                languagePreferences: payload?.languagePreferences || current?.profile?.languagePreferences || null,
              }
            : current?.profile || null,
      }));
      setActiveAccountProfile((current) => ({
        ...(current || {}),
        preferredUiLanguageCode: payload?.preferredUiLanguageCode || current?.preferredUiLanguageCode || locale,
        languagePreferences: payload?.languagePreferences || current?.languagePreferences || null,
      }));
      setLanguageDraft(groupLanguagePreferences({
        languagePreferences: payload?.languagePreferences || {},
      }));
      setLanguageSaveMessage("Language preferences saved.");
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) {
        return;
      }
      setErrorMessage(error instanceof Error ? error.message : tx("Could not save your language preferences."));
    } finally {
      setLanguageSaving(false);
    }
  }

  return (
    <SignedInSettingsShell
      currentSection="account"
      title="Account"
      onBack={() => navigate(-1)}
      planLabel={planLabel}
      planTooltipCopy={activeBillingSummary ? formatSettingsPlanSummary(planCode, activeBillingSummary?.subscription) : null}
      planTooltipLoading={loading || (Boolean(billingRouteAccountId) && !activeBillingSummary && !billingSummaryUnavailable)}
      sectionLinks={{
        preferences: preferencesRoute,
        referrals: referralsRoute,
        billing: billingRoute,
      }}
    >
      <section className="max-w-3xl max-[559px]:sr-only">
        <h1 className="font-display text-3xl font-bold tracking-tight">{tx("Account")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {tx("Manage your Min account, shared billing, and the WhatsApp numbers linked underneath it.")}
        </p>
      </section>

      {relinked ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900">
          {tx("WhatsApp was relinked. Chats will finish indexing in the background.")}
        </section>
      ) : null}

      {errorMessage ? (
        <section className="rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-4 text-sm text-foreground">
          {tx(errorMessage)}
        </section>
      ) : null}

      {loading ? (
        <section className="rounded-3xl border bg-card p-6">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {tx("Loading your account...")}
          </div>
        </section>
      ) : null}

      {!loading ? (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article className="rounded-3xl border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("Email")}</p>
              <p className="mt-3 text-base font-medium text-foreground">
                {email ? <span>{email}</span> : tx("Not available")}
              </p>
            </article>

            <article className="rounded-3xl border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("First name")}</p>
              <p className="mt-3 text-base font-medium text-foreground">
                {profile?.firstName ? <span>{profile.firstName}</span> : tx("Not set")}
              </p>
            </article>

            <article className="rounded-3xl border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("Last name")}</p>
              <p className="mt-3 text-base font-medium text-foreground">
                {profile?.lastName ? <span>{profile.lastName}</span> : tx("Not set")}
              </p>
            </article>

            <article className="rounded-3xl border bg-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("Subscription")}</p>
              {billingSummaryLoading && !activeBillingSummary ? (
                <div className="mt-4 h-10 animate-pulse rounded-2xl bg-muted/60" />
              ) : billingSummaryUnavailable ? (
                <div className="mt-3 space-y-1">
                  <p className="text-base font-medium text-foreground">{tx("Billing unavailable")}</p>
                  <p className="text-sm text-muted-foreground">{tx(billingSummaryError)}</p>
                </div>
              ) : (
                <p className="mt-3 text-base font-medium text-foreground">{tx(`${getPlanLabel(planCode || fallbackPlanCode)} plan`)}</p>
              )}
              <Button className="mt-4 h-10 rounded-xl" variant="outline" onClick={() => navigate(billingRoute)}>
                {tx("Manage billing")}
              </Button>
            </article>
          </section>

          <section className="rounded-3xl border bg-card p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("WhatsApp Numbers")}</p>
                <h2 className="mt-3 font-display text-2xl font-bold text-foreground">{tx("Linked WhatsApp numbers")}</h2>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                  {tx("Billing is shared across your account. Relinks and preferences stay scoped to each WhatsApp number.")}
                </p>
              </div>

              <Button className="h-11 rounded-xl" onClick={() => navigate("/link")}>
                <Plus className="mr-2 h-4 w-4" />
                {tx("Add number")}
              </Button>
            </div>

            {sortedNumbers.length ? (
              <div className="mt-6 space-y-4">
                {sortedNumbers.map((account) => (
                  <article
                    key={account.id}
                    className={`rounded-2xl border px-4 py-4 sm:px-5 ${
                      account.id === activeAccountId ? "border-primary/40 bg-primary/5" : "border-border/70 bg-background"
                    }`}
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-base font-semibold text-foreground">
                            {account.displayName || account.phoneNumber ? <span>{buildAccountLabel(account)}</span> : tx("Linked number")}
                          </p>
                          {account.isPrimary ? (
                            <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]">
                              {tx("Primary")}
                            </Badge>
                          ) : null}
                          {account.id === activeAccountId ? (
                            <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]">
                              {tx("Selected")}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {account.phoneNumber ? <span>{account.phoneNumber}</span> : tx("Phone number not available yet")}
                        </p>
                        <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                          {tx(linkedAccountStatusLabel(account.status))}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {!account.isPrimary ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="h-10 rounded-xl"
                            disabled={primaryMutationAccountId === account.id}
                            onClick={() => void handleMakePrimary(account.id)}
                          >
                            {primaryMutationAccountId === account.id ? tx("Saving...") : tx("Make primary")}
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="outline"
                          className="h-10 rounded-xl"
                          onClick={() => navigate(`${EXISTING_PREFERENCES_PATH}&accountId=${encodeURIComponent(account.id)}`)}
                        >
                          {tx("Edit preferences")}
                        </Button>
                        <Button
                          type="button"
                          className="h-10 rounded-xl"
                          onClick={() => navigate(`/relink?accountId=${encodeURIComponent(account.id)}`)}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          {tx("Relink")}
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
                {tx("No WhatsApp number is linked yet.")}
              </div>
            )}
          </section>
          <FreeExtrasSection />
          {activeAccountId ? (
            <section className="rounded-3xl border bg-card p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("Languages")}</p>
                  <h2 className="mt-3 font-display text-2xl font-bold text-foreground">{tx("Delivery languages")}</h2>
                  <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                    {tx("Choose your home language and any other languages you can receive summaries in. The page language stays in the top-right switcher.")}
                  </p>
                </div>
                <Button
                  className="h-11 rounded-xl"
                  onClick={() => void handleSaveLanguages()}
                  disabled={languageSaving || activeAccountProfileLoading || !activeLanguageProfile}
                >
                  {languageSaving || activeAccountProfileLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {tx("Save languages")}
                </Button>
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)]">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="account-home-language">
                    {tx("Home language")}
                  </label>
                  <Select
                    value={normalizedHomeLanguageCode || HOME_LANGUAGE_UNSET}
                    onValueChange={(nextLanguageCode) => {
                      const normalizedLanguageCode = nextLanguageCode === HOME_LANGUAGE_UNSET
                        ? ""
                        : normalizeLanguageCode(nextLanguageCode, "");
                      setLanguageDraft((current) => ({
                        homeLanguageCode: normalizedLanguageCode,
                        understoodLanguageCodes: current.understoodLanguageCodes.filter(
                          (languageCode) => languageCode !== normalizedLanguageCode,
                        ),
                      }));
                      setLanguageSaveMessage("");
                    }}
                  >
                    <SelectTrigger id="account-home-language" className="h-12 rounded-2xl text-base">
                      <SelectValue placeholder={tx("Choose language")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={HOME_LANGUAGE_UNSET}>{tx("Choose language")}</SelectItem>
                      {languageOptions.map((language) => (
                        <SelectItem key={language.code} value={language.code}>
                          {language.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="text-sm font-medium text-foreground">{tx("Other languages you understand")}</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {tx("Min can use these when someone sends you a voice note from another language.")}
                  </p>
                  <div className="mt-3 rounded-2xl border border-border/70 bg-muted/30 p-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      {otherLanguageOptions.map((language) => {
                        const checked = languageDraft.understoodLanguageCodes.includes(language.code);
                        return (
                          <label
                            key={language.code}
                            className="flex items-center justify-between gap-3 rounded-xl px-1 py-1 text-sm text-foreground"
                          >
                            <span>{language.label}</span>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(nextChecked) => {
                                setLanguageDraft((current) => ({
                                  ...current,
                                  understoodLanguageCodes:
                                    nextChecked === true
                                      ? [...new Set([...current.understoodLanguageCodes, language.code])]
                                      : current.understoodLanguageCodes.filter(
                                        (languageCode) => languageCode !== language.code,
                                      ),
                                }));
                                setLanguageSaveMessage("");
                              }}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {languageSaveMessage ? (
                    <p className="mt-3 text-sm text-muted-foreground">{tx(languageSaveMessage)}</p>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          <Button className="rounded-full" onClick={() => navigate("/agent")}>{tx("Connect my agent")}</Button>
          <DeveloperAccessSection linkedNumbers={sortedNumbers} activeAccountId={activeAccountId} tx={tx} />
          <McpConnectionsSection />
        </>
      ) : null}
    </SignedInSettingsShell>
  );
}
